import { IonMenu, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonFooter, IonLabel } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { RecordingItem } from '@/components/RecordingItem/RecordingItem';
import { TagModal } from '@/components/TagModal/TagModal';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils';
import { ITag } from '@/utils/getFirebaseTag';

import { useActiveSessionStore } from '@/hooks/useActiveSessionStore';
import { useExitSession } from '@/hooks/useExitSession';
import { useSensors } from '@/hooks/useSensors';
import { useSessionScreenStore } from '@/hooks/useSessionScreenStore';

import { useSensorsContext } from '@/contexts/SensorsContext';

import styles from './SensorSidebar.module.css';

import type { Recording } from '@/store/ActiveSessionStore';

interface SensorSidebarProps {
  disabled?: boolean;
  isHidden?: boolean;
  isHubFlow?: boolean;
}

export const SensorSidebar = ({ disabled, isHidden, isHubFlow }: SensorSidebarProps) => {
  const { recordings, updateRecording, deleteRecording, undoDeleteRecording } = useActiveSessionStore();
  const { goToScreen } = useSessionScreenStore();
  const { reconfigureSensors, resetCalibration, state } = useSensorsContext();
  const { stopMeasurement, updateTag } = useSensors(isHubFlow ?? false);
  const { exit } = useExitSession(isHubFlow ?? false);

  const nonDeletedRecordings = useMemo(() => recordings.filter((recording) => !recording.deleted), [recordings]);

  const [showModal, setShowModal] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<Recording>();

  const buttonsAreDisabled = useMemo(() => {
    return state.isRecording;
  }, [state.isRecording]);

  const handleDeleteClick = (recording: Recording) => {
    deleteRecording(recording.id);

    toast({
      icon: 'delete',
      title: 'Recording deleted',
      duration: 8000,
      button: {
        label: 'Undo',
        icon: 'undo',
        onClick: () => {
          undoDeleteRecording(recording.id);
        },
      },
    });
  };

  const handleTagClick = (recording: Recording) => {
    setSelectedRecording(recording);
    setShowModal(true);
  };

  const handleTagModalClose = (changedTags?: ITag[]) => {
    setShowModal(false);

    if (selectedRecording !== undefined) {
      updateRecording({
        ...selectedRecording,
        tags: changedTags,
      });

      if (!changedTags) return;

      // Update local state to reflect the newly changed tags
      for (const tag of changedTags) {
        updateTag(selectedRecording.count, selectedRecording.movementType, tag?.id);
      }
    }
  };

  const handleReconfigureSensorsClick = useCallback(async () => {
    stopMeasurement();
    await reconfigureSensors();
    goToScreen(1);
  }, []);

  const handleEndSession = useCallback(() => {
    if (isHubFlow) {
      exit();
    }
    stopMeasurement();
    goToScreen(3);
  }, []);

  const handleCalibrateSensorsClick = useCallback(async () => {
    resetCalibration();
    goToScreen(-1);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <IonMenu
        className={mergeClassNames(styles.sidebar, disabled && styles.disabled, isHidden && styles.hidden)}
        contentId='main'
        menuId='recording'
        type='push'>
        <IonHeader className={mergeClassNames(styles.header, 'ion-no-border')}>
          <IonToolbar className={styles.toolbar}>
            <IonTitle className={styles.title}>Recordings</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className={styles.content}>
          <IonList className={styles.list}>
            {nonDeletedRecordings.map((recording) => (
              <RecordingItem
                handleDeleteClick={() => handleDeleteClick(recording)}
                handleTagClick={() => handleTagClick(recording)}
                key={recording.id}
                recording={recording}
              />
            ))}
          </IonList>

          {!nonDeletedRecordings.length && (
            <div className={styles.empty}>
              <div className={styles.skeletons}>
                <span />
                <span />
                <span />
                <span />
              </div>

              <div className={styles.emptyTextContainer}>
                <Icon color='var(--ion-color-grey300)' name='walk' size={24} />
                <IonLabel className={styles.emptyText}>No recordings yet</IonLabel>
              </div>
            </div>
          )}
        </IonContent>

        <IonFooter className={styles.footer}>
          {isHubFlow ? (
            <>
              <Button
                disabled={buttonsAreDisabled}
                isFullWidth
                onClick={handleCalibrateSensorsClick}
                variant='secondary'>
                Re-calibrate sensors
              </Button>
              <Button
                className={styles.button}
                disabled={buttonsAreDisabled}
                isFullWidth
                onClick={handleEndSession}
                variant='secondary'>
                End session
              </Button>
            </>
          ) : (
            <Button
              className={styles.button}
              disabled={buttonsAreDisabled}
              isFullWidth
              onClick={handleReconfigureSensorsClick}
              variant='secondary'>
              Re-configure Sensors
            </Button>
          )}
        </IonFooter>
      </IonMenu>

      <TagModal
        handleCancel={handleCancel}
        handleClose={handleTagModalClose}
        initialValue={selectedRecording?.tags}
        isOpen={showModal}
      />
    </>
  );
};
